// src/components/common/ConfirmModal.jsx
//
// Shared confirmation modal for destructive/state-changing actions (delete,
// archive, finalize, invalidate, ...). Extracted from the repeated
// hand-rolled "Delete X" Modal block used by Department/Designation/Task
// Type/Task Status/Task Workflow/TAT Rule/Form Type management pages, so
// every page gets the same look/behavior instead of re-implementing it (and
// so pages that previously used a bare `window.confirm(...)` — Projects,
// Project Modules, Approval Policies, Performance Periods, Performance
// Scorecards — can be upgraded to the same in-app modal pattern).
//
// Uses the existing `unit-modal`/`unit-btn-*`/`unit-delete-text` CSS classes
// (defined once in pages/admins/AdminUnitPage.css, loaded globally by CRA)
// rather than inventing new styling, per the "existing navy/orange theme
// only" instruction.
import React from "react";
import { Modal, Button, Spinner } from "react-bootstrap";

export default function ConfirmModal({
  show,
  onHide,
  onConfirm,
  title = "Confirm",
  message,
  confirmLabel = "Confirm",
  confirmVariantClass = "unit-btn-danger",
  busy = false,
  busyLabel = "Working...",
  // Optional -- Admin-only call sites pass "admin-teal-modal" (or
  // "admin-teal-modal admin-teal-modal-danger" for delete/danger
  // confirmations) to opt into the Teal Blue Admin modal theme. Left empty
  // by default so this component's other, non-Admin callers (shared
  // Forms/Task-approval pages reused by Technician/Developer/etc. routes)
  // are completely unaffected.
  dialogClassName = "",
  // Optional -- element rendered inside a rounded icon circle above the
  // message (see theme/adminTealModal.css's .admin-modal-*-icon classes),
  // e.g. a MUI <DeleteOutline/> for delete or <PauseCircleOutline/> for
  // deactivate confirmations. Purely decorative (aria-hidden); the message
  // text alone still carries the meaning for screen readers.
  icon = null,
  iconClassName = "admin-modal-danger-icon",
}) {
  return (
    <Modal
      show={show}
      onHide={busy ? undefined : onHide}
      centered
      className="unit-modal"
      dialogClassName={dialogClassName || undefined}
      backdropClassName={dialogClassName ? "admin-teal-backdrop" : undefined}
    >
      <Modal.Header closeButton={!busy} closeLabel="Close">
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {icon && (
          <div className={iconClassName} aria-hidden="true">
            {icon}
          </div>
        )}
        <p className={`unit-delete-text${icon ? " text-center" : ""}`}>{message}</p>
      </Modal.Body>
      <Modal.Footer>
        <Button className="unit-btn unit-btn-light" onClick={onHide} disabled={busy}>
          Cancel
        </Button>
        <Button className={`unit-btn ${confirmVariantClass}`} onClick={onConfirm} disabled={busy}>
          {busy ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              {busyLabel}
            </>
          ) : (
            confirmLabel
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
